import type { AxiosResponse } from "axios";

/**
 * Response unwrapping helpers.
 *
 * Services that pipe `res.data` straight through hand the caller whatever
 * the API returned, so a 2xx response with an unexpected body looks like a
 * valid entity. Unwrap responses through these helpers so anything other
 * than the expected payload collapses to `undefined` and callers can guard
 * on it instead of dereferencing a malformed object.
 */

/**
 * Returns the response payload, or `undefined` when the response failed or
 * came back without a body.
 */
export const unwrapData = <T>(
  operation: string,
  response?: AxiosResponse<T> | null,
): T | undefined => {
  if (!response) {
    console.error(`${operation}: no response`);
    return undefined;
  }
  const { status, data } = response;
  if (status < 200 || status >= 300) {
    console.error(`${operation}: request failed with status ${status}`, data);
    return undefined;
  }
  if (data === undefined || data === null || (data as unknown) === "") {
    console.error(`${operation}: empty response body`);
    return undefined;
  }
  return data;
};

/**
 * Returns the payload only when it is an object carrying every one of
 * `keys` with a non-nullish value, otherwise `undefined`.
 */
export const unwrapEntity = <T extends object>(
  operation: string,
  response: AxiosResponse<T> | null | undefined,
  keys: (keyof T)[],
): T | undefined => {
  const data = unwrapData<T>(operation, response);
  if (!data || typeof data !== "object") return undefined;
  const missing = keys.filter(
    (key) => data[key] === undefined || data[key] === null,
  );
  if (missing.length > 0) {
    console.error(`${operation}: response missing ${missing.join(", ")}`, data);
    return undefined;
  }
  return data;
};
